// MCHD Room Scheduler - Session Times API Client
// Frontend API client for room session time operations

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';


// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface RoomSessionTime {
  id: string;
  roomId: string;
  name: string;
  startTime: string;
  endTime: string;
  bufferMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Building {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

export interface Room {
  id: string;
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  floor: {
    id: string;
    name: string;
    building: {
      id: string;
      name: string;
    };
  };
}

export interface RoomSessionTimeWithDetails extends RoomSessionTime {
  room: {
    id: string;
    number: string;
    name: string;
    floor: {
      id: string;
      name: string;
      building: {
        id: string;
        name: string;
      };
    };
  };
}

export interface CreateRoomSessionTimeData {
  roomId: string;
  name: string;
  startTime: string;
  endTime: string;
  bufferMinutes?: number;
  isActive?: boolean;
}

export interface UpdateRoomSessionTimeData {
  name?: string;
  startTime?: string;
  endTime?: string;
  bufferMinutes?: number;
  isActive?: boolean;
}

export interface GetRoomSessionTimesParams {
  page?: number;
  limit?: number;
  search?: string;
  roomId?: string;
  buildingId?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'startTime' | 'endTime' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface GetRoomSessionTimeStatsParams {
  roomId?: string;
  buildingId?: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedRoomSessionTimesResponse {
  data: RoomSessionTimeWithDetails[];
  pagination: PaginationMeta;
}

export interface RoomSessionTimeStats {
  totalSessionTimes: number;
  activeSessionTimes: number;
  inactiveSessionTimes: number;
  sessionsByRoom: {
    roomId: string;
    roomNumber: string;
    roomName: string;
    sessionCount: number;
  }[];
  sessionsByBuilding: {
    buildingId: string;
    buildingName: string;
    sessionCount: number;
  }[];
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    message?: string;
    timestamp?: string;
  };
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Build query string from parameters
 * @param params - Query parameters
 * @returns Query string
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
}

/**
 * Make authenticated API request
 * @param url - Request URL
 * @param options - Fetch options
 * @returns Promise with response data
 */
async function apiRequest<T>(url: string, token: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  const responseData = await response.json();
  
  // Extract data from API response wrapper
  if (responseData.success && responseData.data !== undefined) {
    return responseData.data;
  }
  
  // Fallback for responses that don't follow the standard format
  return responseData;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all room session times with pagination and filtering
 * @param params - Query parameters
 * @returns Promise with paginated session times
 */
export async function getRoomSessionTimes(
  params: GetRoomSessionTimesParams = {},
  token: string
): Promise<PaginatedRoomSessionTimesResponse> {
  const queryString = buildQueryString(params);
  const url = `${API_BASE_URL}/session-times${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<PaginatedRoomSessionTimesResponse>(url, token);
}

/**
 * Get room session time by ID
 * @param id - Session time ID
 * @returns Promise with session time details
 */
export async function getRoomSessionTimeById(id: string, token: string): Promise<RoomSessionTimeWithDetails> {
  const url = `${API_BASE_URL}/session-times/${id}`;
  
  return apiRequest<RoomSessionTimeWithDetails>(url, token);
}

/**
 * Get room session times by room ID
 * @param roomId - Room ID
 * @returns Promise with session times array
 */
export async function getRoomSessionTimesByRoom(roomId: string, token: string): Promise<RoomSessionTimeWithDetails[]> {
  const url = `${API_BASE_URL}/session-times/room/${roomId}`;
  
  return apiRequest<RoomSessionTimeWithDetails[]>(url, token);
}

/**
 * Create a new room session time
 * @param data - Session time data
 * @returns Promise with created session time
 */
export async function createRoomSessionTime(
  data: CreateRoomSessionTimeData,
  token: string
): Promise<RoomSessionTimeWithDetails> {
  const url = `${API_BASE_URL}/session-times`;
  
  return apiRequest<RoomSessionTimeWithDetails>(url, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update a room session time
 * @param id - Session time ID
 * @param data - Update data
 * @returns Promise with updated session time
 */
export async function updateRoomSessionTime(
  id: string,
  data: UpdateRoomSessionTimeData,
  token: string
): Promise<RoomSessionTimeWithDetails> {
  const url = `${API_BASE_URL}/session-times/${id}`;
  
  return apiRequest<RoomSessionTimeWithDetails>(url, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a room session time
 * @param id - Session time ID
 * @returns Promise with deletion result
 */
export async function deleteRoomSessionTime(id: string, token: string): Promise<RoomSessionTimeWithDetails> {
  const url = `${API_BASE_URL}/session-times/${id}`;
  
  return apiRequest<RoomSessionTimeWithDetails>(url, token, {
    method: 'DELETE',
  });
}

/**
 * Get room session time statistics
 * @param params - Query parameters
 * @returns Promise with statistics
 */
export async function getRoomSessionTimeStats(
  params: GetRoomSessionTimeStatsParams = {},
  token: string
): Promise<RoomSessionTimeStats> {
  const queryString = buildQueryString(params);
  const url = `${API_BASE_URL}/session-times/stats${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<RoomSessionTimeStats>(url, token);
}

// =============================================================================
// UTILITY FUNCTIONS FOR UI
// =============================================================================

/**
 * Format session time for display
 * @param sessionTime - Session time object
 * @returns Formatted string
 */
export function formatSessionTime(sessionTime: RoomSessionTime): string {
  return `${sessionTime.name} (${sessionTime.startTime} - ${sessionTime.endTime})`;
}

/**
 * Format session time duration
 * @param startTime - Start time (HH:MM)
 * @param endTime - End time (HH:MM)
 * @returns Duration in hours and minutes
 */
export function formatSessionDuration(startTime: string, endTime: string): string {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const durationMinutes = endMinutes - startMinutes;
  
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  
  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
}

/**
 * Convert time string (HH:MM) to minutes since midnight
 * @param timeString - Time in HH:MM format
 * @returns Minutes since midnight
 */
function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Get session time status color
 * @param isActive - Active status
 * @returns Color string
 */
export function getSessionTimeStatusColor(isActive: boolean): string {
  return isActive ? 'green' : 'red';
}

/**
 * Validate session time data
 * @param data - Session time data
 * @returns Validation result
 */
export function validateSessionTimeData(data: CreateRoomSessionTimeData | UpdateRoomSessionTimeData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  // Validate time format
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  
  if ('startTime' in data && data.startTime) {
    if (!timeRegex.test(data.startTime)) {
      errors.push('Start time must be in HH:MM format (24-hour)');
    }
  }
  
  if ('endTime' in data && data.endTime) {
    if (!timeRegex.test(data.endTime)) {
      errors.push('End time must be in HH:MM format (24-hour)');
    }
  }
  
  // Validate time logic
  if ('startTime' in data && 'endTime' in data && data.startTime && data.endTime) {
    const startMinutes = timeToMinutes(data.startTime);
    const endMinutes = timeToMinutes(data.endTime);
    
    if (startMinutes >= endMinutes) {
      errors.push('Start time must be before end time');
    }
  }
  
  // Validate buffer minutes
  if ('bufferMinutes' in data && data.bufferMinutes !== undefined) {
    if (data.bufferMinutes < 0 || data.bufferMinutes > 480) {
      errors.push('Buffer minutes must be between 0 and 480 (8 hours)');
    }
  }
  
  // Validate name
  if ('name' in data && data.name) {
    if (data.name.length < 1 || data.name.length > 50) {
      errors.push('Session name must be between 1 and 50 characters');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}
